import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
// import { apiEndpoints, handleApiError } from '../services/api';
import { toast } from 'react-hot-toast';

import { createApiUrlWithPrefix, createApiUrl, getHorseImageUrl } from '../config/api';
import { API_BASE_URL } from '../config/api';
import {
  X,
  Plus,
  Edit,
  Trash2,
  Camera,
  Save,
  RefreshCw,
  Calendar,
  MapPin,
  User,
  Zap
} from 'lucide-react';

// Import pagination component
import Pagination from '../components/Pagination';
import Modal, { ModalFooterBar } from './ui/Modal';

function HorseManagementModal({ isOpen, onClose, owner, onHorseCreated, onHorseUpdated, onHorseDeleted }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingHorse, setEditingHorse] = useState(null);
  const [riders, setRiders] = useState([]);

  // Form states
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    box: '',
    birthDate: '',
    entryDate: new Date().toISOString().split('T')[0], // Today's date
    riderIds: [],
    imageFile: null,
    documentFile: null
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10; // Fixed page size as requested
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Fetch horses for the owner with pagination
  const fetchHorses = useCallback(async () => {
    if (!owner?.id) return;
    
    try {
      setLoading(true);
      const response = await axios.get(createApiUrlWithPrefix(`horses/owner/${owner.id}?page=${currentPage}&limit=${limit}${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ''}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      // API might return { horses, ... } or just an array
      const raw = Array.isArray(response.data) ? response.data : (response.data.horses || []);
      const horsesData = raw.filter((h) => h != null && h.id != null);
      setHorses(horsesData);
      setTotalItems(response.data.totalItems || horsesData.length);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching horses:', error);
      toast.error('Failed to fetch horses');
    } finally {
      setLoading(false);
    }
  }, [owner?.id, currentPage, searchTerm]);

  // Fetch available riders
  const fetchRiders = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/horses/riders`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setRiders(response.data);
    } catch (error) {
      console.error('Error fetching riders:', error);
      toast.error('Failed to fetch riders');
    }
  };

  useEffect(() => {
    if (isOpen && owner?.id) {
      fetchHorses();
      fetchRiders();
    }
  }, [isOpen, owner?.id, fetchHorses]);

  // Reset page when modal opens or owner changes
  useEffect(() => {
    setCurrentPage(1);
  }, [owner?.id, isOpen, searchTerm]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        imageFile: file
      }));
    }
  };

  const handleDocumentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        documentFile: file
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      box: '',
      birthDate: '',
      entryDate: new Date().toISOString().split('T')[0],
      riderIds: [],
      imageFile: null,
      documentFile: null
    });
  };

  const handleCreateHorse = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error('Horse name is required');
        return;
      }

      const horseData = {
        name: formData.name,
        box: formData.box || null,
        entryDate: formData.entryDate ? new Date(formData.entryDate).toISOString() : new Date().toISOString(),
        ownerRides: true,
        ownerId: owner.id,
        riderIds: formData.riderIds.map(id => parseInt(id)),
        birthDate: formData.birthDate ? new Date(formData.birthDate).toISOString() : null
      };

      console.log('Creating horse with data:', horseData);
      console.log('Form data:', formData);
      console.log('Entry date from form:', formData.entryDate);
      console.log('Owner:', owner);
      console.log('Token:', localStorage.getItem('token') ? 'Present' : 'Missing');

      const response = await axios.post(`${API_BASE_URL}/api/horses`, horseData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const newHorse = response.data;

      // Upload image if provided
      if (formData.imageFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('image', formData.imageFile);

        // Do NOT set Content-Type manually - let axios set it with boundary
        await axios.post(createApiUrlWithPrefix(`horses/upload-image/${newHorse.id}`), formDataUpload, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      }

      // Upload document if provided
      if (formData.documentFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('document', formData.documentFile);

        // Do NOT set Content-Type manually - let axios set it with boundary
        await axios.post(createApiUrlWithPrefix(`horses/upload-document/${newHorse.id}`), formDataUpload, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      }

      toast.success('Horse created successfully');
      resetForm();
      setShowCreateModal(false);
      fetchHorses();
      if (onHorseCreated) onHorseCreated();
    } catch (error) {
      console.error('Error creating horse:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      const errorMessage = error.response?.data?.message || error.response?.data || 'Failed to create horse';
      toast.error(errorMessage);
    }
  };

  const handleEditHorse = (horse) => {
    setEditingHorse(horse);
    setFormData({
      name: horse.name,
      box: horse.box || '',
      entryDate: horse.entryDate ? new Date(horse.entryDate).toISOString().split('T')[0] : '',
      birthDate: horse.birthDate ? new Date(horse.birthDate).toISOString().split('T')[0] : '',
      riderIds: horse.riderIds ? horse.riderIds.map(id => id.toString()) : [],
      imageFile: null,
      documentFile: null
    });
    setShowEditModal(true);
  };

  const handleUpdateHorse = async () => {
    if (!editingHorse?.id) return;
    try {
      if (!formData.name.trim()) {
        toast.error('Horse name is required');
        return;
      }

      const updateData = {
        name: formData.name,
        box: formData.box || null,
        entryDate: formData.entryDate ? new Date(formData.entryDate).toISOString() : new Date().toISOString(),
        ownerRides: true,
        riderIds: formData.riderIds.map(id => parseInt(id)),
        birthDate: formData.birthDate ? new Date(formData.birthDate).toISOString() : null,
        imagePath: editingHorse?.imagePath || null,
        documentPath: editingHorse?.documentPath || null,
        description: editingHorse?.description || null,
        breed: editingHorse?.breed || null,
        status: editingHorse?.status
      };

      console.log('Updating horse with data:', updateData);
      console.log('Form data:', formData);
      console.log('Entry date from form:', formData.entryDate);

      await axios.put(createApiUrlWithPrefix(`horses/${editingHorse.id}`), updateData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      // Upload new image if provided
      if (formData.imageFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('image', formData.imageFile);

        // Do NOT set Content-Type manually - let axios set it with boundary
        await axios.post(createApiUrlWithPrefix(`horses/upload-image/${editingHorse.id}`), formDataUpload, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      }

      // Upload new document if provided
      if (formData.documentFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('document', formData.documentFile);

        // Do NOT set Content-Type manually - let axios set it with boundary
        await axios.post(createApiUrlWithPrefix(`horses/upload-document/${editingHorse.id}`), formDataUpload, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      }

      toast.success('Horse updated successfully');
      resetForm();
      setShowEditModal(false);
      setEditingHorse(null);
      fetchHorses();
      if (onHorseUpdated) onHorseUpdated();
    } catch (error) {
      console.error('Error updating horse:', error);
      toast.error('Failed to update horse');
    }
  };

  const handleDeleteHorse = async (horseId) => {
    if (window.confirm('Are you sure you want to delete this horse? This action cannot be undone.')) {
      try {
        await axios.delete(createApiUrlWithPrefix(`horses/${horseId}`), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        toast.success('Horse deleted successfully');
        fetchHorses();
        if (onHorseDeleted) onHorseDeleted();
      } catch (error) {
        console.error('Error deleting horse:', error);
        toast.error('Failed to delete horse');
      }
    }
  };

  if (!isOpen || !owner) return null;

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('common.horseManagement')}
      subtitle={owner.name}
      icon={User}
      variant="emerald"
      size="4xl"
      dir={isRTL ? 'rtl' : 'ltr'}
      badges={[`${totalItems} horse${totalItems !== 1 ? 's' : ''}`]}
      footer={
        <ModalFooterBar hint="Add, edit, or remove horses for this owner.">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 text-sm font-medium bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
          >
            Close
          </button>
        </ModalFooterBar>
      }
    >

          {/* Search + Add button row */}
          <div className="mb-4 flex items-center space-x-2">
            <input
              type="text"
              placeholder={t('common.searchHorses')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-grow px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            <button
              onClick={() => { resetForm(); setShowCreateModal(true); }}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('common.addHorse')}
            </button>
          </div>

          {/* Horses Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-600">Loading horses...</span>
            </div>
          ) : horses.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600">{t('common.noHorsesFound')}</p>
              <p className="text-sm text-gray-500">{t('horses.clickToAddHorse')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {horses.map((horse) => {
                if (!horse) return null;
                return (
                <div key={horse.id} className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  {/* Horse Image */}
                  <div className="relative h-48 bg-gray-100 rounded-t-xl overflow-hidden">
                    {horse?.imagePath ? (
                      <img
                        src={getHorseImageUrl(horse.imagePath)}
                        alt={horse.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div className={`w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center ${horse?.imagePath ? 'hidden' : 'flex'}`}>
                      <Zap className="w-12 h-12 text-gray-400" />
                    </div>
                    {/* Status Badge */}
                    <div className="absolute top-3 right-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        horse.status === 0 ? 'bg-green-100 text-green-800' :
                        horse.status === 1 ? 'bg-yellow-100 text-yellow-800' :
                        horse.status === 2 ? 'bg-gray-100 text-gray-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {horse.status === 0 ? 'Active' : horse.status === 1 ? 'Inactive' : horse.status === 2 ? 'Retired' : 'Sold'}
                      </span>
                    </div>
                  </div>

                  {/* Horse Info */}
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{horse.name}</h3>
                    <div className="space-y-2 text-sm text-gray-600">
                      {horse.box && (
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                          <span>{t('common.boxNumber')}: {horse.box}</span>
                        </div>
                      )}
                      {horse.entryDate && (
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                          <span>{t('common.entryDate')}: {new Date(horse.entryDate).toLocaleDateString()}</span>
                        </div>
                      )}
                      {horse.birthDate && (
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                          <span>{t('common.birthDate')}: {new Date(horse.birthDate).toLocaleDateString()}</span>
                        </div>
                      )}
                      {horse.age && (
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                          <span>Age: {horse.age} years</span>
                        </div>
                      )}
                      {horse.documentPath && (
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <a href={getHorseImageUrl(horse.documentPath)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 text-sm">
                            {t('common.viewDocument')}
                          </a>
                        </div>
                      )}
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-2 text-gray-400" />
                        <span className={horse.ownerRides ? 'text-green-600 font-medium' : 'text-blue-600 font-medium'}>
                          {horse.ownerRides ? t('common.riddenByOwner') : (horse.riderNames?.length > 0 ? `${t('common.riddenBy')} ${horse.riderNames.join(', ')}` : t('common.riddenByRiders'))}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 rounded-b-xl">
                    <div className="flex items-center justify-center space-x-2">
                      <button
                        onClick={() => handleEditHorse(horse)}
                        className="flex items-center px-3 py-1.5 text-sm text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded-lg transition-colors"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        {t('common.edit')}
                      </button>
                      <button
                        onClick={() => handleDeleteHorse(horse.id)}
                        className="flex items-center px-3 py-1.5 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        {t('common.delete')}
                      </button>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex justify-center">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={limit}
                showInfo={true}
              />
            </div>
          )}
          )}
    </Modal>

      {/* ── Create Horse Modal ── */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); resetForm(); }}
        title="Add New Horse"
        subtitle={owner.name}
        icon={Plus}
        variant="emerald"
        size="lg"
        zIndex={60}
        bodyClassName="p-0"
        footer={
          <ModalFooterBar>
            <button
              type="button"
              onClick={() => { setShowCreateModal(false); resetForm(); }}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleCreateHorse}
              className="px-5 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {t('common.addHorse')}
            </button>
          </ModalFooterBar>
        }
      >
        <div className="p-4 sm:p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left – Basic Info */}
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3 border-b border-gray-200 pb-2">Basic Information</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Horse Name *</label>
                        <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder={t('horses.enterHorseName')} />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.boxNumber')}</label>
                          <input type="text" name="box" value={formData.box} onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            placeholder={t('horses.boxNumber')} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.entryDate')}</label>
                          <input type="date" name="entryDate" value={formData.entryDate} onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.birthDate')}</label>
                        <input type="date" name="birthDate" value={formData.birthDate} onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3 border-b border-gray-200 pb-2">Riding Information</h4>
                    <div className="space-y-3">
                      <p className="text-xs text-gray-500 mb-2">Select one or more riders for this horse. Leave empty if only the owner rides.</p>
                      {true && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.selectRider')}</label>
                          <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-2">
                            {riders.length === 0 ? (
                              <p className="text-sm text-gray-500 p-2">No eligible riders found (must be Active with paid membership)</p>
                            ) : (
                              riders.map((rider) => {
                                const riderIdStr = rider.id.toString();
                                const isSelected = formData.riderIds.includes(riderIdStr);
                                return (
                                  <label key={rider.id} className={`flex items-center p-2 rounded-lg cursor-pointer transition-colors ${
                                    isSelected ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50 border border-transparent'
                                  }`}>
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => {
                                        setFormData(prev => ({
                                          ...prev,
                                          riderIds: isSelected
                                            ? prev.riderIds.filter(id => id !== riderIdStr)
                                            : [...prev.riderIds, riderIdStr]
                                        }));
                                      }}
                                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="ml-2 text-sm font-medium text-gray-700">
                                      {rider.name || `${rider.firstName} ${rider.lastName}`}
                                    </span>
                                  </label>
                                );
                              })
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Select one or more riders for this horse</p>
                          {formData.riderIds.length > 0 && (
                            <p className="text-xs text-green-600 mt-1 font-medium">
                              {formData.riderIds.length} rider{formData.riderIds.length > 1 ? 's' : ''} selected
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right – File Uploads */}
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3 border-b border-gray-200 pb-2">File Uploads</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Horse Image</label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                          <Camera className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                          <label className="cursor-pointer">
                            <span className="text-sm font-medium text-green-600 hover:text-green-500">{t('common.uploadImage')}</span>
                            <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                          </label>
                          <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 10MB</p>
                          {formData.imageFile && <p className="text-xs text-green-600 mt-1">Selected: {formData.imageFile.name}</p>}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Horse Document (PDF)</label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                          <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <label className="cursor-pointer">
                            <span className="text-sm font-medium text-green-600 hover:text-green-500">Upload PDF</span>
                            <input type="file" accept=".pdf" onChange={handleDocumentChange} className="hidden" />
                          </label>
                          <p className="text-xs text-gray-500 mt-1">PDF up to 10MB</p>
                          {formData.documentFile && <p className="text-xs text-green-600 mt-1">Selected: {formData.documentFile.name}</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
        </div>
      </Modal>

      {/* ── Edit Horse Modal ── */}
      <Modal
        isOpen={showEditModal && !!editingHorse}
        onClose={() => { setShowEditModal(false); setEditingHorse(null); resetForm(); }}
        title={t('common.editHorse')}
        subtitle={editingHorse?.name}
        icon={Edit}
        variant="amber"
        size="lg"
        zIndex={60}
        bodyClassName="p-0"
        footer={
          <ModalFooterBar>
            <button
              type="button"
              onClick={() => { setShowEditModal(false); setEditingHorse(null); resetForm(); }}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleUpdateHorse}
              className="px-5 py-2 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {t('common.save')}
            </button>
          </ModalFooterBar>
        }
      >
        <div className="p-4 sm:p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left – Basic Info */}
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3 border-b border-gray-200 pb-2">Basic Information</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Horse Name *</label>
                        <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder={t('horses.enterHorseName')} />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.boxNumber')}</label>
                          <input type="text" name="box" value={formData.box} onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            placeholder={t('common.boxPlaceholder')} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.entryDate')}</label>
                          <input type="date" name="entryDate" value={formData.entryDate} onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.birthDate')}</label>
                        <input type="date" name="birthDate" value={formData.birthDate} onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3 border-b border-gray-200 pb-2">Riding Information</h4>
                    <div className="space-y-3">
                      <p className="text-xs text-gray-500 mb-2">Select one or more riders for this horse. Leave empty if only the owner rides.</p>
                      {true && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.selectRider')}</label>
                          <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-2">
                            {riders.length === 0 ? (
                              <p className="text-sm text-gray-500 p-2">No eligible riders found (must be Active with paid membership)</p>
                            ) : (
                              riders.map((rider) => {
                                const riderIdStr = rider.id.toString();
                                const isSelected = formData.riderIds.includes(riderIdStr);
                                return (
                                  <label key={rider.id} className={`flex items-center p-2 rounded-lg cursor-pointer transition-colors ${
                                    isSelected ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50 border border-transparent'
                                  }`}>
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => {
                                        setFormData(prev => ({
                                          ...prev,
                                          riderIds: isSelected
                                            ? prev.riderIds.filter(id => id !== riderIdStr)
                                            : [...prev.riderIds, riderIdStr]
                                        }));
                                      }}
                                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="ml-2 text-sm font-medium text-gray-700">
                                      {rider.name || `${rider.firstName} ${rider.lastName}`}
                                    </span>
                                  </label>
                                );
                              })
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Select one or more riders for this horse</p>
                          {formData.riderIds.length > 0 && (
                            <p className="text-xs text-green-600 mt-1 font-medium">
                              {formData.riderIds.length} rider{formData.riderIds.length > 1 ? 's' : ''} selected
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right – File Uploads */}
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3 border-b border-gray-200 pb-2">File Uploads</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Horse Image</label>
                        {editingHorse?.imagePath && (
                          <div className="mb-3">
                            <img src={getHorseImageUrl(editingHorse.imagePath)} alt={editingHorse?.name}
                              className="w-16 h-16 object-cover rounded-lg border" />
                            <p className="text-xs text-gray-500 mt-1">Current image</p>
                          </div>
                        )}
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                          <Camera className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                          <label className="cursor-pointer">
                            <span className="text-sm font-medium text-green-600 hover:text-green-500">Upload New Image</span>
                            <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                          </label>
                          <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 10MB</p>
                          {formData.imageFile && <p className="text-xs text-green-600 mt-1">Selected: {formData.imageFile.name}</p>}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Horse Document (PDF)</label>
                        {editingHorse?.documentPath && (
                          <div className="mb-3">
                            <div className="flex items-center p-2 bg-gray-50 rounded-lg border">
                              <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <a href={getHorseImageUrl(editingHorse.documentPath)} target="_blank" rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 text-sm">
                                {t('common.viewCurrentDocument')}
                              </a>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Current document</p>
                          </div>
                        )}
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                          <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <label className="cursor-pointer">
                            <span className="text-sm font-medium text-green-600 hover:text-green-500">Upload New PDF</span>
                            <input type="file" accept=".pdf" onChange={handleDocumentChange} className="hidden" />
                          </label>
                          <p className="text-xs text-gray-500 mt-1">PDF up to 10MB</p>
                          {formData.documentFile && <p className="text-xs text-green-600 mt-1">Selected: {formData.documentFile.name}</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
        </div>
      </Modal>
    </>
  );
}

export default HorseManagementModal;
